import { makeStyles } from "tss-react/mui"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from "@mui/material"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useState } from "react"
import { enqueueSnackbar } from "notistack"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { checkInGuest } from "../../serviceCenterHomeApi"
import { ServiceCenterMeetingQuery } from "../../meetings/meetingApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { MeetingAttendee } from "../../meetings/meetingTypes"
import { AttendeeType } from "../../serviceCenterHomeTypes"
import { useCurrentServiceCenter } from "../../../../store/serviceCenterHooks"

const useStyles = makeStyles()(() => ({
    modal: {
        padding: "3rem",
    },
    mainContainer: {
        width: "60%",
    },
    checkInButton: {
        padding: ".5rem 3rem !important",
    },
    label: {
        border: "1px solid #CBD5E1",
        borderRadius: "0.75rem",
        width: "100%",
        padding: "0.625rem 0.75rem ",
    },
}))

type GuestCheckInModalProps = {
    guests: Array<MeetingAttendee>
    open: boolean
    onClose: () => void
}

export function GuestCheckInModal({ open, onClose, guests }: GuestCheckInModalProps) {
    const { classes } = useStyles()
    const { serviceCenterId } = useCurrentServiceCenter()

    const [notifyHost, setNotifyHost] = useState(true)
    const [printGuestCard, setPrintGuestCard] = useState(true)
    const { l10n } = useAppLocalization()

    const { mutateAsync } = useApiMutation(checkInGuest, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterMeetingQuery.SERVICE_CENTER_MEETING_DETAILS],
            })

            const successMessage =
                typeof data === "object" && "serviceResponse" in data && data?.serviceResponse?.displayMessage
                    ? data?.serviceResponse.displayMessage
                    : l10n.getString("service-center-reports-check-success-title")

            enqueueSnackbar({
                variant: "success",
                message: successMessage,
            })
            onClose()
        },
        async onError(_, error) {
            const errorMsg =
                error && typeof error === "object" && "displayMessage" in error
                    ? (error?.displayMessage as string)
                    : "Something went wrong. Try again."

            enqueueSnackbar({
                variant: "error",
                message: errorMsg,
            })
        },
    })

    const handleCheckIn = async () => {
        const data: AttendeeType[] = guests.map((guest) => ({
            type: "ATTENDEE",
            attendee: {
                id: guest.id,
            },
            guest: {
                company: guest.companyName,
                name: guest.name,
            },
        }))
        await mutateAsync({
            serviceCenterId: serviceCenterId ?? 0,
            checkIns: data,
            notifyHost: notifyHost,
        })
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="service-center-reports-check-in-modal-guest-check-in-title">
                    <>Guest check-in</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp={"md"}
            scroll="body"
        >
            <Box className={classes.modal}>
                <Stack direction="row" alignItems="start" spacing="5rem" mb="3rem">
                    <Box className={classes.mainContainer}>
                        <LocalizedStrict
                            id="service-center-reports-check-in-modal-guest-check-in-subtitle"
                            vars={{ amount: guests.length }}
                        >
                            <Typography variant="h6" mb="2rem">
                                Guests to check in ({guests.length})
                            </Typography>
                        </LocalizedStrict>
                        <Stack direction="column" spacing="1rem" mb="3rem">
                            {guests.map((guest, index) => (
                                <Grid key={index} container gap=".5rem">
                                    <Grid item xs={5.9} md={5.9}>
                                        <Typography className={classes.label}>{guest.name}</Typography>
                                    </Grid>
                                    <Grid item xs={5.9} md={5.9}>
                                        <Typography className={classes.label}>{guest.companyName}</Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="h6">Settings</Typography>
                        <Stack direction="column">
                            <LocalizedStrict
                                id="service-center-reports-check-in-modal-print-guest-card"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={printGuestCard}
                                            onChange={(e) => setPrintGuestCard(e.target.checked)}
                                        />
                                    }
                                    label="Print guest card"
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="service-center-reports-check-in-modal-send-host-notification"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    control={
                                        <LocalizedStrict
                                            attrs={{ title: true }}
                                            id="service-center-reports-check-in-modal-send-host-notification-tooltip"
                                        >
                                            <Checkbox
                                                checked={notifyHost}
                                                onChange={(e) => setNotifyHost(e.target.checked)}
                                            />
                                        </LocalizedStrict>
                                    }
                                    label="Notify host"
                                />
                            </LocalizedStrict>
                        </Stack>
                    </Box>
                </Stack>
                <Stack alignItems="center" justifyContent="center">
                    <LocalizedStrict id="service-center-reports-check-in-modal-check-in-button">
                        <StandardButton className={classes.checkInButton} fullWidth={false} onClick={handleCheckIn}>
                            Check-in
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </Box>
        </StandardDialog>
    )
}
